import {
  ColumnDef,
  SortConfig,
  TableModel as ITableModel,
  RowValidator,
  CellUpdateHandler,
  RowUpdateHandler,
} from './types';
import { ValidationResult, validResult, invalidResult } from './DataType';
import { ChangeTracker, SaveChangesHandler, PreviewChangesHandler } from './ChangeTracker';

/**
 * Default implementation of TableModel
 */
export class DefaultTableModel<T = unknown> implements ITableModel<T> {
  columns: ColumnDef<T>[];
  data: T[];
  sortConfig?: SortConfig;
  columnFilters: Map<string, unknown>;
  selectedRowIndex?: number;
  searchText?: string;
  editingCell?: { rowIndex: number; columnId: string };
  rowValidator?: RowValidator<T>;
  onCellUpdate?: CellUpdateHandler<T>;
  onRowUpdate?: RowUpdateHandler<T>;
  validationErrors: Map<string, ValidationResult>;
  changeTracker?: ChangeTracker<T>;
  getRowId?: (row: T) => string | number;
  saveChanges?: SaveChangesHandler<T>;
  previewChanges?: PreviewChangesHandler<T>;
  // The rows as last loaded or saved: what Discard puts back. Edits change data
  // in place (an optimistic update), so the tracker alone cannot restore it.
  private savedData: T[];

  constructor(
    columns: ColumnDef<T>[],
    data: T[],
    options?: {
      enableChangeTracking?: boolean;
      getRowId?: (row: T) => string | number;
      saveChanges?: SaveChangesHandler<T>;
      /** what a save would run, shown in the save dialog before saving */
      previewChanges?: PreviewChangesHandler<T>;
    }
  ) {
    this.columns = columns;
    this.data = data;
    this.savedData = data;
    this.columnFilters = new Map();
    this.selectedRowIndex = undefined;
    this.searchText = undefined;
    this.editingCell = undefined;
    this.validationErrors = new Map();
    this.getRowId = options?.getRowId;
    this.saveChanges = options?.saveChanges;
    this.previewChanges = options?.previewChanges;

    if (options?.enableChangeTracking) {
      if (!options.getRowId) {
        throw new Error('getRowId is required when enableChangeTracking is true');
      }
      this.changeTracker = new ChangeTracker(options.getRowId);
    }
  }

  getRow(index: number): T | undefined {
    const sortedData = this.getFilteredData();
    return sortedData[index];
  }

  getRowCount(): number {
    return this.data.length;
  }

  getVisibleColumns(): ColumnDef<T>[] {
    return this.columns.filter(col => col.visible !== false);
  }

  getColumn(columnId: string): ColumnDef<T> | undefined {
    return this.columns.find(col => col.id === columnId);
  }

  updateColumn(columnId: string, updates: Partial<ColumnDef<T>>): void {
    const index = this.columns.findIndex(col => col.id === columnId);
    if (index !== -1) {
      const newColumns = [...this.columns];
      newColumns[index] = { ...newColumns[index], ...updates };
      this.columns = newColumns;
    }
  }

  reorderColumns(columnIds: string[]): void {
    const columnMap = new Map(this.columns.map(col => [col.id, col]));
    const reordered: ColumnDef<T>[] = [];

    columnIds.forEach(id => {
      const col = columnMap.get(id);
      if (col) {
        reordered.push(col);
        columnMap.delete(id);
      }
    });

    // Add any remaining columns that weren't in the reorder list
    columnMap.forEach(col => reordered.push(col));

    this.columns = reordered;
  }

  setSortConfig(config: SortConfig | undefined): void {
    this.sortConfig = config;
  }

  getSortedData(): T[] {
    if (!this.sortConfig) {
      return this.data;
    }

    const column = this.getColumn(this.sortConfig.columnId);
    if (!column) {
      return this.data;
    }

    const sorted = [...this.data];
    const { direction } = this.sortConfig;
    const multiplier = direction === 'asc' ? 1 : -1;

    // Priority: sortFn > dataType.compare > default
    if (column.sortFn) {
      sorted.sort((a, b) => column.sortFn!(a, b) * multiplier);
    } else if (column.dataType) {
      sorted.sort((a, b) => {
        const aVal = column.accessor(a);
        const bVal = column.accessor(b);
        return column.dataType!.compare(aVal, bVal) * multiplier;
      });
    } else {
      sorted.sort((a, b) => {
        const aVal = column.accessor(a);
        const bVal = column.accessor(b);

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * multiplier;
        }

        return (aVal < bVal ? -1 : 1) * multiplier;
      });
    }

    return sorted;
  }

  setColumnFilter(columnId: string, filterState: unknown): void {
    if (filterState == null || (filterState instanceof Set && filterState.size === 0)) {
      this.columnFilters.delete(columnId);
    } else {
      this.columnFilters.set(columnId, filterState);
    }
  }

  clearColumnFilter(columnId: string): void {
    this.columnFilters.delete(columnId);
  }

  clearAllFilters(): void {
    this.columnFilters.clear();
  }

  getFilteredData(): T[] {
    let filtered = this.data;

    // Apply all column filters
    if (this.columnFilters.size > 0) {
      filtered = this.data.filter(row => {
        // Row must pass all filters
        for (const [columnId, filterState] of this.columnFilters.entries()) {
          const column = this.getColumn(columnId);
          if (!column || !column.filter) continue;

          const value = column.accessor(row);
          if (!column.filter.matches(value, filterState)) {
            return false; // Row doesn't pass this filter
          }
        }
        return true; // Row passes all filters
      });
    }

    // Then apply sorting
    return this.applySorting(filtered);
  }

  private applySorting(data: T[]): T[] {
    if (!this.sortConfig) {
      return data;
    }

    const column = this.getColumn(this.sortConfig.columnId);
    if (!column) {
      return data;
    }

    const sorted = [...data];
    const { direction } = this.sortConfig;
    const multiplier = direction === 'asc' ? 1 : -1;

    // Priority: sortFn > dataType.compare > default
    if (column.sortFn) {
      sorted.sort((a, b) => column.sortFn!(a, b) * multiplier);
    } else if (column.dataType) {
      sorted.sort((a, b) => {
        const aVal = column.accessor(a);
        const bVal = column.accessor(b);
        return column.dataType!.compare(aVal, bVal) * multiplier;
      });
    } else {
      sorted.sort((a, b) => {
        const aVal = column.accessor(a);
        const bVal = column.accessor(b);

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * multiplier;
        }

        return (aVal < bVal ? -1 : 1) * multiplier;
      });
    }

    return sorted;
  }

  setSelectedRow(rowIndex: number | undefined): void {
    this.selectedRowIndex = rowIndex;
  }

  getSelectedRow(): T | undefined {
    if (this.selectedRowIndex === undefined) {
      return undefined;
    }
    return this.getRow(this.selectedRowIndex);
  }

  setSearchText(text: string): void {
    this.searchText = text?.trim() || undefined;
  }

  getSearchFilteredData(): T[] {
    let filtered = this.data;

    // First apply global search
    if (this.searchText && this.searchText.length > 0) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(row => {
        // Check if any column value matches the search text
        for (const column of this.columns) {
          const value = column.accessor(row);
          if (value != null) {
            const stringValue = String(value).toLowerCase();
            if (stringValue.includes(searchLower)) {
              return true;
            }
          }
        }
        return false;
      });
    }

    // Then apply column filters
    if (this.columnFilters.size > 0) {
      filtered = filtered.filter(row => {
        // Row must pass all filters
        for (const [columnId, filterState] of this.columnFilters.entries()) {
          const column = this.getColumn(columnId);
          if (!column || !column.filter) continue;

          const value = column.accessor(row);
          if (!column.filter.matches(value, filterState)) {
            return false; // Row doesn't pass this filter
          }
        }
        return true; // Row passes all filters
      });
    }

    // Finally apply sorting
    return this.applySorting(filtered);
  }

  startEditCell(rowIndex: number, columnId: string): void {
    this.editingCell = { rowIndex, columnId };
  }

  cancelEdit(): void {
    this.editingCell = undefined;
  }

  async commitCellEdit(rowIndex: number, columnId: string, newValue: unknown): Promise<ValidationResult> {
    const row = this.getRow(rowIndex);
    if (!row) {
      return invalidResult(['Row not found']);
    }

    const column = this.getColumn(columnId);
    if (!column) {
      return invalidResult(['Column not found']);
    }

    const oldValue = column.accessor(row);

    // Validate with dataType if available
    let result: ValidationResult = validResult();
    if (column.dataType?.validate) {
      result = column.dataType.validate(newValue);
      if (!result.valid) {
        this.validationErrors.set(`${rowIndex}:${columnId}`, result);
        return result;
      }
    }

    // Validate with column validator if available
    if (column.validator) {
      result = column.validator(newValue, row);
      if (!result.valid) {
        this.validationErrors.set(`${rowIndex}:${columnId}`, result);
        return result;
      }
    }

    // Validate with row validator if available
    if (this.rowValidator) {
      const tempRow = column.setter ? column.setter(row, newValue) : { ...row, [columnId]: newValue };
      result = this.rowValidator(tempRow as T, columnId);
      if (!result.valid) {
        this.validationErrors.set(`${rowIndex}:${columnId}`, result);
        return result;
      }
    }

    // Clear any previous errors
    this.clearCellErrors(rowIndex, columnId);

    // Check if value actually changed (skip noop changes)
    const valueChanged = oldValue !== newValue;

    // Track change if change tracking is enabled and value changed
    if (this.changeTracker) {
      if (valueChanged) {
        this.changeTracker.trackUpdate(row, columnId, oldValue, newValue);

        // Update the data locally (optimistic update)
        const filteredData = this.getFilteredData();
        const actualRow = filteredData[rowIndex];
        const dataIndex = this.data.indexOf(actualRow);
        if (dataIndex !== -1) {
          const newData = [...this.data];
          if (column.setter) {
            newData[dataIndex] = column.setter(newData[dataIndex], newValue);
          } else {
            // Default: set property directly
            newData[dataIndex] = { ...newData[dataIndex], [columnId]: newValue };
          }
          this.changeTracker.rowReplaced(actualRow, newData[dataIndex]);
          this.data = newData;
        }
      }

      this.editingCell = undefined;
      return validResult();
    }

    // Without change tracking, call update handler immediately
    if (valueChanged) {
      if (this.onCellUpdate) {
        result = await Promise.resolve(this.onCellUpdate(row, columnId, newValue, oldValue));
        if (!result.valid) {
          this.validationErrors.set(`${rowIndex}:${columnId}`, result);
          return result;
        }
      }

      // Update the data
      const filteredData = this.getFilteredData();
      const actualRow = filteredData[rowIndex];
      const dataIndex = this.data.indexOf(actualRow);
      if (dataIndex !== -1) {
        const newData = [...this.data];
        if (column.setter) {
          newData[dataIndex] = column.setter(newData[dataIndex], newValue);
        } else {
          // Default: set property directly
          newData[dataIndex] = { ...newData[dataIndex], [columnId]: newValue };
        }
        this.data = newData;
      }
    }

    this.editingCell = undefined;
    return validResult();
  }

  startEditRow(rowIndex: number): void {
    // For form mode editing
    this.selectedRowIndex = rowIndex;
  }

  async commitRowEdit(rowIndex: number, updates: Partial<T>): Promise<ValidationResult> {
    const row = this.getRow(rowIndex);
    if (!row) {
      return invalidResult(['Row not found']);
    }

    const updatedRow = { ...row, ...updates } as T;

    // Validate each updated column
    for (const [key, value] of Object.entries(updates)) {
      const column = this.getColumn(key);
      if (column?.dataType?.validate) {
        const result = column.dataType.validate(value);
        if (!result.valid) {
          this.validationErrors.set(`${rowIndex}:${key}`, result);
          return result;
        }
      }

      if (column?.validator) {
        const result = column.validator(value, updatedRow);
        if (!result.valid) {
          this.validationErrors.set(`${rowIndex}:${key}`, result);
          return result;
        }
      }
    }

    // Validate with row validator if available
    if (this.rowValidator) {
      const result = this.rowValidator(updatedRow);
      if (!result.valid) {
        // Store error for the whole row
        this.validationErrors.set(`${rowIndex}:__row__`, result);
        return result;
      }
    }

    // Clear any previous errors for this row
    for (const key of Object.keys(updates)) {
      this.clearCellErrors(rowIndex, key);
    }
    this.validationErrors.delete(`${rowIndex}:__row__`);

    // Call update handler if available
    if (this.onRowUpdate) {
      const result = await Promise.resolve(this.onRowUpdate(row, updates));
      if (!result.valid) {
        this.validationErrors.set(`${rowIndex}:__row__`, result);
        return result;
      }
    }

    // Update the data
    const filteredData = this.getFilteredData();
    const actualRow = filteredData[rowIndex];
    const dataIndex = this.data.indexOf(actualRow);
    if (dataIndex !== -1) {
      this.data[dataIndex] = updatedRow;
    }

    return validResult();
  }

  getCellErrors(rowIndex: number, columnId: string): ValidationResult | undefined {
    return this.validationErrors.get(`${rowIndex}:${columnId}`);
  }

  clearCellErrors(rowIndex: number, columnId: string): void {
    this.validationErrors.delete(`${rowIndex}:${columnId}`);
  }

  insertRow(row: T, index?: number): void {
    const newData = [...this.data];
    if (index !== undefined && index >= 0 && index <= newData.length) {
      newData.splice(index, 0, row);
    } else {
      newData.push(row);
    }
    this.data = newData;

    if (this.changeTracker) {
      this.changeTracker.trackInsert(row);
    }
  }

  /** Returns the staged delete's row id when change tracking recorded one (not for a dropped insert). */
  deleteRow(rowIndex: number): string | number | undefined {
    const filteredData = this.getFilteredData();
    const row = filteredData[rowIndex];
    if (!row) return undefined;

    const dataIndex = this.data.indexOf(row);
    if (dataIndex !== -1) {
      const newData = [...this.data];
      newData.splice(dataIndex, 1);
      this.data = newData;

      if (this.changeTracker) {
        return this.changeTracker.trackDelete(row);
      }
    }
    return undefined;
  }

  hasUnsavedChanges(): boolean {
    return this.changeTracker?.hasChanges() ?? false;
  }

  /** Throw away every staged change and show the rows as last loaded or saved. */
  discardChanges(): void {
    if (this.changeTracker) {
      this.changeTracker.discard();
    }
    this.data = this.savedData;
    this.editingCell = undefined;
    this.validationErrors.clear();
  }

  /** The staged changes were saved: the current rows are the new baseline. */
  acceptChanges(): void {
    this.changeTracker?.clear();
    this.savedData = this.data;
  }
}
